import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/_components/dialog";
import { Button } from "@/components/_components/button";
import { Skeleton } from "@/components/_components/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/_components/table";
import { Download } from "lucide-react";
import { formatDateTime } from "@/lib/utils";
import { toast } from "react-hot-toast";
import { useEffect } from "react";

interface ChatHistoryItem {
  id?: number;
  correlationId: string;
  recipientId?: number;
  channel: string;
  status: string;
  userMessage: string;
  receivedAt: string;
  imResponse: string;
  respondedAt: string;
  responseTime?: number;
  aiRequestAt?: string;
  aiResponseAt?: string;
  aiResponseTime?: number;
  createdAt?: string;
}

interface ChatHistoryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  chatHistoryData: ChatHistoryItem[];
  recipientData?: {
    fullName?: string;
    emailId?: string;
    mobilePhoneNo?: string;
    status?: string;
    outcome?: string;
  };
  transId?: string;
  onDownloadCSV?: () => void;
}

const ChatHistoryDialog = ({
  isOpen,
  onClose,
  isLoading,
  chatHistoryData,
  recipientData,
  transId,
  onDownloadCSV,
}: ChatHistoryDialogProps) => {
  const downloadChatHistoryCSV = () => {
    if (!chatHistoryData || chatHistoryData.length === 0) {
      toast.error("No chat history to download");
      return;
    }

    // Prepare metadata
    const metadata = [
      ["# Chat History Export"],
      [`# Generated on: ${new Date().toISOString()}`],
      [`# Transaction ID: ${transId || "N/A"}`],
      [`# Recipient: ${recipientData?.fullName || "Unknown"}`],
      [`# Email: ${recipientData?.emailId || "N/A"}`],
      [`# Phone: ${recipientData?.mobilePhoneNo || "N/A"}`],
      [`# Status: ${recipientData?.status || "N/A"}`],
      [`# Outcome: ${recipientData?.outcome || "N/A"}`],
      [`# Total Conversations: ${chatHistoryData.length}`],
      [""], // Empty row for separation
    ];

    // Prepare CSV headers
    const csvHeaders = [
      "ID",
      "Recipient_ID",
      "Correlation_ID",
      "Comm_Type",
      "Status",
      "User_Request",
      "User_Request_Length",
      "AI_Response",
      "AI_Response_Length",
      "Request_At",
      "Request_Date",
      "Request_Time",
      "Response_At",
      "Response_Date",
      "Response_Time",
      "AI_Request_At",
      "AI_Response_At",
      "Total_Response_Time_Seconds",
      "AI_Processing_Time_Seconds",
      "Created_At"
    ];

    // Prepare CSV data rows
    const csvRows = chatHistoryData.map((item, index) => {
      const requestAt = item.receivedAt ? new Date(item.receivedAt) : null;
      const responseAt = item.respondedAt ? new Date(item.respondedAt) : null;
      const aiRequestAt = item.aiRequestAt ? new Date(item.aiRequestAt) : null;
      const aiResponseAt = item.aiResponseAt ? new Date(item.aiResponseAt) : null;
      const createdAt = item.createdAt ? new Date(item.createdAt) : null;

      // Calculate response times in seconds
      let totalResponseTimeSeconds = "";
      let aiProcessingTimeSeconds = "";

      if (requestAt && responseAt) {
        const diffMs = responseAt.getTime() - requestAt.getTime();
        totalResponseTimeSeconds = Math.round(diffMs / 1000).toString();
      }

      if (aiRequestAt && aiResponseAt) {
        const diffMs = aiResponseAt.getTime() - aiRequestAt.getTime();
        aiProcessingTimeSeconds = Math.round(diffMs / 1000).toString();
      }

      return [
        (item.id || index + 1).toString(),
        (item.recipientId || "").toString(),
        item.correlationId || "",
        item.channel || "webchat",
        item.status || "",
        `"${(item.userMessage || "").replace(/"/g, '""')}"`, // Escape quotes
        (item.userMessage || "").length.toString(),
        `"${(item.imResponse || "").replace(/"/g, '""')}"`, // Escape quotes
        (item.imResponse || "").length.toString(),
        requestAt ? requestAt.toISOString() : "",
        requestAt ? requestAt.toLocaleDateString() : "",
        requestAt ? requestAt.toLocaleTimeString() : "",
        responseAt ? responseAt.toISOString() : "",
        responseAt ? responseAt.toLocaleDateString() : "",
        responseAt ? responseAt.toLocaleTimeString() : "",
        aiRequestAt ? aiRequestAt.toISOString() : "",
        aiResponseAt ? aiResponseAt.toISOString() : "",
        totalResponseTimeSeconds,
        aiProcessingTimeSeconds,
        createdAt ? createdAt.toISOString() : ""
      ];
    });

    // Create CSV content with metadata and data
    const csvContent = [
      ...metadata.map(row => row.join(",")),
      csvHeaders.join(","),
      ...csvRows.map(row => row.join(","))
    ].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);

      // Generate filename with timestamp and recipient info
      const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const recipientIdentifier = (recipientData?.fullName || recipientData?.emailId || recipientData?.mobilePhoneNo || "unknown")
        .replace(/[^a-zA-Z0-9]/g, "_");
      const filename = `chat-history-${transId || "unknown"}-${recipientIdentifier}-${timestamp}.csv`;

      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      URL.revokeObjectURL(url);

      toast.success(`Chat history downloaded as ${filename}`);
    } else {
      toast.error("Download not supported in this browser");
    }
  };

  const handleDownload = () => {
    if (onDownloadCSV) {
      onDownloadCSV();
    } else {
      downloadChatHistoryCSV();
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        handleDownload();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, chatHistoryData, onDownloadCSV]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-none max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>Chat History</DialogTitle>
              <DialogDescription>
                View conversation history between the recipient and AI assistant
              </DialogDescription>
            </div>
            {chatHistoryData && chatHistoryData.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 border-blue-300 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-600 flex items-center gap-1"
                title="Download chat history as CSV (Ctrl+Shift+D)"
              >
                <Download className="h-3 w-3" />
                Download CSV
              </Button>
            )}
          </div>
        </DialogHeader>
        <div className="space-y-4">
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ) : chatHistoryData && chatHistoryData.length > 0 ? (
            <>
              {/* Chat History Summary */}
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg space-y-2">
                <div className="text-sm">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Conversations:</span>
                  <span className="ml-2 text-gray-900 dark:text-gray-100">{chatHistoryData.length}</span>
                </div>
                {recipientData && (
                  <>
                    <div className="text-sm">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Recipient:</span>
                      <span className="ml-2 text-gray-900 dark:text-gray-100">{recipientData.fullName}</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Contact:</span>
                      <span className="ml-2 text-gray-900 dark:text-gray-100">
                        {recipientData.emailId && `📧 ${recipientData.emailId}`}
                        {recipientData.emailId && recipientData.mobilePhoneNo && " | "}
                        {recipientData.mobilePhoneNo && `📱 ${recipientData.mobilePhoneNo}`}
                      </span>
                    </div>
                  </>
                )}
              </div>

              <div className="overflow-x-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50 dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800">
                      <TableHead className="font-semibold text-gray-900 dark:text-gray-100 w-16">ID</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-gray-100 min-w-[200px]">Correlation ID</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-gray-100 w-24">Comm Type</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-gray-100 w-24">Status</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-gray-100 min-w-[200px]">User Request</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-gray-100 min-w-[250px]">AI Response</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-gray-100 w-40">Response Time</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-gray-100 w-40">Request At</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-gray-100 w-40">Response At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {chatHistoryData.map((item, index) => (
                      <TableRow key={item.id || item.correlationId} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <TableCell className="font-medium text-center">{item.id || index + 1}</TableCell>
                        <TableCell className="font-mono text-xs">
                          <div className="break-all">
                            {item.correlationId}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                            {item.channel}
                          </span>
                        </TableCell>
                        <TableCell>
                          {item.status || '-'}
                        </TableCell>
                        <TableCell>
                          <div className="max-w-[300px]">
                            <div className="text-sm text-gray-900 dark:text-gray-100 break-words whitespace-pre-wrap p-2 bg-blue-50 dark:bg-blue-950 rounded border-l-4 border-blue-400 dark:border-blue-600">
                              {item.userMessage || '-'}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-[350px]">
                            <div className="text-sm text-gray-900 dark:text-gray-100 break-words whitespace-pre-wrap p-2 bg-green-50 dark:bg-green-950 rounded border-l-4 border-green-400 dark:border-green-600">
                              {item.imResponse || '-'}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-[200px]">
                            <div className="space-y-1">
                              <div className="text-sm font-medium text-blue-600 dark:text-blue-400">
                                Total: {item.responseTime ? (item.responseTime / 1000).toFixed(2) + 's' : 'N/A'}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                AI: {item.aiResponseTime ? (item.aiResponseTime / 1000).toFixed(2) + 's' : 'N/A'}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          <div className="space-y-1">
                            <div className="font-medium">{item.receivedAt ? formatDateTime(item.receivedAt) : '-'}</div>
                            {item.aiRequestAt && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                                AI Start: {formatDateTime(item.aiRequestAt)}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          <div className="space-y-1">
                            <div className="font-medium">{item.respondedAt ? formatDateTime(item.respondedAt) : '-'}</div>
                            {item.aiResponseAt && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                                AI End: {formatDateTime(item.aiResponseAt)}
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* CSV Download Info */}
              <div className="text-xs text-gray-500 dark:text-gray-400 bg-blue-50 dark:bg-blue-950 p-3 rounded-lg">
                <strong>💡 CSV Export includes:</strong>
                <ul className="mt-1 space-y-1 list-disc list-inside">
                  <li>Complete conversation data with unique IDs and correlation IDs</li>
                  <li>Communication type (webchat, email, etc.)</li>
                  <li>User requests and AI responses with character counts</li>
                  <li>Detailed timestamps (request, response, AI processing times)</li>
                  <li>Response time calculations in seconds</li>
                  <li>Recipient and session metadata</li>
                </ul>
                <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-800">
                  <strong>⌨️ Tip:</strong> Use <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">Ctrl+Shift+D</kbd> for quick download
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No chat history available
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChatHistoryDialog;